import { createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';
import Map from '@/router/map';
import { getApptimize, getCookie } from '@/app-instance';

import assetAssociations from '@routes/assetAssociations';
import attachments from '@routes/attachments';
import bookmarks from '@routes/bookmarks';
import calculator from './routes/calculator';
import catalog from '@routes/catalog';
import categories from '@routes/categories';
import checkout from '@routes/checkout';
import contentPartners from '@routes/content-partners';
import dashboards from '@routes/mastery/dashboards';
import decks from '@routes/decks';
import deeplinks from '@routes/deeplinks';
import following from '@routes/following';
import groupings from '@routes/groupings';
import interactiveCaseStudies from '@routes/interactiveCaseStudies';
import learningModules from '@routes/learningModules';
import learningPath from '@routes/learningPath';
import library from '@routes/library';
import mnemonics from '@routes/mnemonics';
import navigationGroups from '@routes/navigationGroups';
import profile from '@routes/profile';
import progress from '@routes/progress';
import progressReports from '@routes/progressReports';
import questionOfTheDay from '@routes/questionOfTheDay';
import questionSets from '@routes/questionSets';
import quizzes from '@routes/quizzes';
import scenarios from '@routes/scenarios';
import search from '@routes/search';
import studyPlanner from '@routes/studyPlanner';
import discussionBoard from '@routes/discussionBoard';
import sims from '@routes/sims';
import singletons from '@routes/singletons';
import social from '@routes/social';
import testSimulations from '@routes/testSimulations';
import testing from '@routes/testing';
import topics from '@routes/topics';
import userCertifications from '@routes/userCertifications';
import webPages from '@routes/webPages';
import lessons from '@routes/lessons';

import masterySingletons from '@routes/mastery/singletons';

let routes = [
  ...singletons,
  ...quizzes,
  ...userCertifications,
  ...assetAssociations,
  ...attachments,
  ...bookmarks,
  ...calculator,
  ...catalog,
  ...categories,
  ...checkout,
  ...contentPartners,
  ...dashboards,
  ...decks,
  ...deeplinks,
  ...following,
  ...groupings,
  ...interactiveCaseStudies,
  ...learningModules,
  ...learningPath,
  ...library,
  ...mnemonics,
  ...navigationGroups,
  ...profile,
  ...progress,
  ...progressReports,
  ...questionOfTheDay,
  ...questionSets,
  ...scenarios,
  ...search,
  ...studyPlanner,
  ...discussionBoard,
  ...sims,
  ...social,
  ...testSimulations,
  ...testing,
  ...topics,
  ...webPages,
  ...masterySingletons,
  ...lessons,
].map((route, index) => {
  // routes with component as promise
  if (typeof route.component === 'function') {
    // get function with promise returned
    let fn = route.component;
    // reassign route to dynamically import with added catch
    route.component = () => {
      // get promise from function
      let promise = fn();
      // reload the current page to fetch fresh chunks
      promise.catch(e => window.location.reload());
      return promise;
    };
  }
  return route;
});

import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { shouldEnterOnboarding } from '@/utils/onboarding';
import { shouldEnterQuestionnaire } from '@/utils/questionnaire';
import { scheduleTour } from '@/utils/tour';

let attempted = false;
let loadPromise;
let store;

function setupRouter() {
  const router = createRouter({
    history: createWebHistory(),
    routes: routes,
    // Taken from vue router docs
    // savedPosition will exist if user has used browser back/forward buttons
    // to restore scroll position. Otherwise, scroll to the top on route change
    // https://router.vuejs.org/guide/advanced/scroll-behavior.html
    scrollBehavior (to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.name == 'topics#show' && from.name == 'topics#show'){
        // possible special case where we don't want to scroll the screen because
        // the user may be just changing the selected subtopic.
        if (to.params.categoryId == from.params.categoryId &&
          to.params.topicId == from.params.topicId) {
          return null;
        }
      }
      return { left: 0, top: 0 };
    },
  });

  router.beforeEach(checkLastActive);
  router.beforeEach(rejectInvalidRoutes);
  router.beforeEach(loadApp);
  router.beforeEach(loadUser);
  router.beforeEach(redirectDeeplinks);
  router.beforeEach(validateResource);
  router.beforeEach(authorize);
  router.beforeEach(guardUserQuizAccess);
  router.beforeEach(mastery);
  router.beforeEach(setTitle);
  router.beforeEach(bigBrother);
  router.beforeEach(previewRoute);

  const ajax = applyCaseMiddleware(axios.create({
    baseURL: apiUrl,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'HLTUserTimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  }));

  // Here we want to override the axios-case-converter transform defined here: https://github.com/mpyw/axios-case-converter/blob/master/src/middleware.ts#L81
  // But we are only doing it for the apps AJAX request, so we just make an interceptor to reset the axios instance default setting

  ajax.interceptors.request.use(function (config) {
    if (config.url === '/apps') {
      config.transformResponse = axios.defaults.transformResponse;
    }

    return config;
  });

  ajax.interceptors.response.use(undefined, function (error) {
    const code = (error.response || {}).status;

    if (code === 301 && error.response?.data) {
      return window.location.replace(error.response.data);
    }

    // when user is not logged in
    if (code === 401) {
      // when user should be logged in based on API request (exception is load user call)
      if (!(error.response.config.method === 'get' && error.response.config.url.slice(-5) === '/user')) {
        getCookie().set('redirectAfterLogin', router.currentRoute.value?.fullPath || '/');
        store.dispatch('session/signOut');
        return new Promise(() => {});
      }
    }

    // when user is logged in and attempts access and is denied
    if (code === 403) {
      router.push({ name: 'home' });
      return new Promise(() => {});
    }

    return Promise.reject(error);
  });

  store.commit('session/configureAjax', ajax);

  return router;
}

const apiUrl = (function() {
  let url = '';
  if (process.env.NODE_ENV === 'development') {
    if (process.env.SKIP_SSL) {
      url = `http://${process.env.API_URL}:${process.env.API_PORT}`;
    } else  {
      url = `https://${process.env.API_URL}:${process.env.API_PORT}`;
    }
  } else {
    if (window.location.host.includes('ada.org')) {
      url = 'https://hlt-api.ada.org';
    } else {
      url = `https://${process.env.API_URL}`;
    }
  }

  return url += `/api/web/${process.env.API_VERSION}`;
})();

async function waitForLoadPromise(promise, next, to, redirect) {
  if (loadPromise) {
    return await loadPromise.then((res) => {
      redirect ? next(redirect) : next();
    });
  } else {
    redirect ? next(redirect) : next();
  }
}

function checkLastActive(to, from, next) {
  if (store.getters['session/inactive']) {
    location.reload();
  } else {
    store.commit('session/lastActive');
  }
  next();
}

function rejectInvalidRoutes(to, from, next) {
  if (to.matched.length < 1) {
    Sentry.withScope(scope => {
      scope.setLevel('info');
      scope.setExtra('path', to.fullPath);
      Sentry.captureMessage('Invalid Route');
    });
    next({name: 'home'});
  } else {
    next();
  }
}

function loadApp(to, from, next) {
  if (!store.getters['app/appLoaded']) {
    loadPromise = store.dispatch('app/loadApp').then(() => {
      // iterate over attribution params and save cookies of them
      store.getters['session/trackingAttributes'].map(item => {
        if (to.query.hasOwnProperty(item)) {
          getCookie().set(item, to.query[item]);
        }
      });
    });

  }
  next();
}

function loadUser(to, from, next) {
  if (store.getters['session/authenticated']) {
    waitForLoadPromise(loadPromise, next, to);
  } else if (to.query.hasOwnProperty('beta_token')) {
    store.dispatch('session/signIn', [to.query, loadPromise]).then(() => {
      // if user came from sign up
      const modifiedNext = function() {
        if (to.query.hasOwnProperty('signup')) {
          // see if they should have a tour scheduled
          scheduleTour(store);
        }

        next();
      };

      waitForLoadPromise(loadPromise, modifiedNext, to);
    }).catch(e => {
      attempted = true;
      waitForLoadPromise(loadPromise, next, to);
    });
  } else if (!attempted) {
    store.dispatch('session/loadUser', loadPromise).catch(e => {
      attempted = true;
    }).finally(() => {
      waitForLoadPromise(loadPromise, next, to);
    });
  } else {
    waitForLoadPromise(loadPromise, next, to);
  }
}

function redirectDeeplinks(to, from, next) {
  // params coming from API link:
  // destination is the string name of the navigation destination
  // id is the resource id that correlates to that navigation destination
  if (to.name === 'deeplink') {
    const destination = to.query.navigation_destination;
    const supportedDeeplinks = [
      'card',
      'deck',
      'flashcard',
      'navigation_group',
      'purchase_order',
      'qotd',
      'topic',
      'upgrade',
    ];

    // this destination should just start "study all" in first nav item in main nav group
    if (destination === 'flashcard_quickstart') {
      let item = store.getters['navigationItems/menuItems'].find(ni => ni.destination === 'flashcard_categories');
      if (item) {
        next({ name: 'categories#flashcard', params: { categoryId: 'all' }, query: { nav: item.query } });
      } else {
        next({ name: 'home' });
      }
    } else if (supportedDeeplinks.includes(destination)) {
      let map = Map.deeplinks[destination];
      let route = map.name;
      let qs = map.query || {};
      qs.discussionId = to.query.discussion_id;

      next({ name: route, params: map.params(to.query.id), query: qs });
    } else {
      next({ name: 'home' });
    }
  } else {
    next();
  }
}

// make sure the nav query param is valid
function validateResource(to, from, next) {
  let nav = to.query.nav;
  if (nav) {
    nav = nav.split(',').slice(0).pop();
    if (nav) {
      const id = nav.split('-')[0];
      const item = store.getters['navigationItems/navigationItems'][id];
      if (!item) {
        Sentry.withScope(scope => {
          scope.setLevel('info');
          scope.setExtra('path', to.fullPath);
          Sentry.captureMessage('Invalid Nav Query');
        });
        return next({name: 'home'});
      }
    }
  }

  next();
}

const QUIZ_FLASHCARD_ROUTE_NAMES = new Set([
  'quizzes#flashcard',
  'quizzes#flashcard-v2',
  'quizzes#results',
  'quizzes#results#question',
]);

function guardUserQuizAccess(to, from, next) {
  if (QUIZ_FLASHCARD_ROUTE_NAMES.has(to.name)) {
    const categoryId = to.params.categoryId;
    if (!categoryId) {
      return next({ name: 'home' });
    }
    const category = store.getters['categories/categories'][categoryId];
    const currentUserId = store.getters['user/get']?.id;
    if (!category) {
      return next({ name: 'home' });
    }
    if (category.userId != null && category.userId !== currentUserId) {
      return next({ name: 'home' });
    }
  }
  next();
}

function authorize(to, from, next) {
  if (!store.getters['session/authenticated'] && !to.meta.public) {
    getCookie().set('redirectAfterLogin', to.fullPath);
    next({name: 'signIn'});
  } else if (to.name !== 'confirmation' && to.query.hasOwnProperty('confirmation') && to.query.hasOwnProperty('ids')) {
    next({name: 'confirmation', query: to.query});
  } else if (to.query.hasOwnProperty('simulation')) {
    const simNavItem = store.getters['navigationItems/menuItems'].find(ni => ni.destination === 'test_simulations');
    next({name: 'testSimulations#congratulations', params: { categoryId: to.query.simulation }, query: { nav: simNavItem?.query }});
  } else if (shouldEnterOnboarding(store) && to.name !== 'onboarding' && to.name !== 'mastery#questionnaire') {
    next({ name: 'onboarding' });
  } else if (shouldEnterQuestionnaire(store, getApptimize()) && to.name !== 'onboarding' && to.name !== 'mastery#questionnaire') {
    next({ name: 'mastery#questionnaire' });
  } else if (!to.meta.public && !to.meta.resourceOptional && !store.getters['navigationItems/menuItems'].find(ni => ni.query === (to.query.nav || '').split(',')[0]) && !store.getters['app/isMasteryApp']) {
    next({name: 'home'});
  } else if (store.getters['navigationItems/defaultNavigationItem'] && to.name === 'home') {
    // Preserve query params when redirecting to default navigation item
    const defaultRoute = store.getters['navigationItems/defaultNavigationItem'].route;
    next({...defaultRoute, query: {...(defaultRoute.query || {}), ...to.query}});
  } else {
    next();
  }
}

function mastery(to, from, next) {
  if (store.getters['app/isMasteryApp'] && to.meta.mastery) {
    next({
      name: `mastery#${to.name}`,
      params: to.params,
      query: to.query,
      hash: to.hash,
    });
  } else {
    next();
  }
}

function bigBrother(to, from, next) {
  try {
    if (window.rtp) {
      window.rtp('send','view');
    }
    if (window.Munchkin) {
      window.Munchkin.munchkinFunction('clickLink', { href: next.fullPath });
    }
  } catch {
  }
  next();
}

function previewRoute(to, from, next) {
  if (to.meta.preview) {
    const user = store.getters['session/currentUser'];
    if (user && !user.previewer) {
      next({ name: 'home' });
    } else {
      next();
    }
  } else {
    next();
  }
}

function setTitle(to, from, next) {
  document.title = to.meta.title || store.getters['app/appName'];
  next();
}

export default (appInstance, vuex) => {
  store = vuex;
  attempted = false;
  loadPromise = null;
  return setupRouter();
};
